import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Beaker } from 'lucide-react';

const MODES = {
  work: {
    label: 'Work',
    color: '#FF4D00',
    icon: Briefcase,
    description: 'Professional decisions',
  },
  labs: {
    label: 'Labs',
    color: '#7C3AED',
    icon: Beaker,
    description: 'Creative experiments',
  },
};

const COOKIE_KEY = 'fray_mode';

function getInitialMode() {
  if (typeof document === 'undefined') return 'work';
  const cookie = document.cookie.split('; ').find(c => c.startsWith(COOKIE_KEY + '='));
  if (cookie) return cookie.split('=')[1];
  // Enterprise users default to work mode
  return 'work';
}

function setModeCookie(mode) {
  // #825 H10: SameSite=strict + Secure (when HTTPS). Pure client-side
  // preference cookie, never POSTed cross-origin, so strict is safe.
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? ';secure' : '';
  document.cookie = `${COOKIE_KEY}=${mode};path=/;max-age=${365 * 24 * 60 * 60};samesite=strict${secure}`;
}

export default function ModeToggle({ variant = 'nav', className = '', onChange }) {
  const [mode, setMode] = useState(getInitialMode);

  const toggleMode = useCallback(() => {
    const next = mode === 'work' ? 'labs' : 'work';
    setMode(next);
    setModeCookie(next);
    onChange?.(next);

    // Analytics event
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'mode_toggle', { mode: next });
    }
  }, [mode, onChange]);

  useEffect(() => {
    // Set CSS custom property for mode-aware theming
    document.documentElement.setAttribute('data-mode', mode);
  }, [mode]);

  const current = MODES[mode];
  const other = MODES[mode === 'work' ? 'labs' : 'work'];

  if (variant === 'full') {
    return (
      <div className={`flex flex-col items-center gap-4 ${className}`}>
        <div className="flex items-center gap-1 p-1 bg-[#0A0A0A] border border-[rgba(245,241,232,0.08)]">
          {Object.entries(MODES).map(([key, m]) => (
            <button
              key={key}
              onClick={() => {
                setMode(key);
                setModeCookie(key);
                onChange?.(key);
              }}
              className="relative flex items-center gap-2 px-5 py-2.5 text-xs font-mono tracking-wider uppercase transition-all"
              style={{ color: mode === key ? '#0A0A0A' : 'rgba(245,241,232,0.48)' }}
            >
              {mode === key && (
                <motion.div
                  layoutId="mode-bg-full"
                  className="absolute inset-0"
                  style={{ backgroundColor: m.color }}
                  transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                />
              )}
              <span className="relative flex items-center gap-2">
                <m.icon className="w-3.5 h-3.5" />
                {m.label}
              </span>
            </button>
          ))}
        </div>
        <p className="text-xs text-[rgba(245,241,232,0.48)] font-body">
          {current.description}
        </p>
      </div>
    );
  }

  // Nav variant — compact pill toggle
  return (
    <button
      onClick={toggleMode}
      className={`group relative flex items-center gap-1.5 px-2.5 py-1.5 border border-[rgba(245,241,232,0.08)] hover:border-[rgba(245,241,232,0.16)] transition-all ${className}`}
      title={`Switch to ${other.label} mode`}
    >
      <div
        className="w-1.5 h-1.5 rounded-full transition-colors"
        style={{ backgroundColor: current.color }}
      />
      <span className="text-[10px] font-mono text-[rgba(245,241,232,0.72)] tracking-wider uppercase">
        {current.label}
      </span>
      <div className="w-px h-3 bg-[rgba(245,241,232,0.08)] mx-0.5" />
      <other.icon className="w-3 h-3 text-[rgba(245,241,232,0.32)] group-hover:text-[rgba(245,241,232,0.48)] transition-colors" />
    </button>
  );
}

export { MODES, getInitialMode };
